// @flow
import * as React from 'react';
import {useEffect, useState, useReducer} from 'react';

import {useApi} from 'docs/data';
import {loadStripe} from '@stripe/stripe-js';

import Box from 'sail/Box';
import Card from 'sail/Card';
import Group from 'sail/Group';
import Text from 'sail/Text/Text';
import Spinner from 'sail/Spinner';
import Icon from 'sail/Icon';

import {Elements, PaymentElement} from '@stripe/react-stripe-js';
import {defineMessages, useIntl} from 'react-intl';

import {THEMES, LOCATIONS} from './constants';
import SourceCodeBlock from './SourceCodeBlock';
import LocationDropdown from './LocationDropdown';
import {reducer, getInitialState} from './state';
import type {State} from './state';
import './PaymentElementAppearanceDemo.scss';

const messages = defineMessages({
  unexpectedError: {
    id: 'docsApp.demo.paymentElement.appearance.unexpectedError',
    description: 'Unexpected error message',
    defaultMessage: "An error occurred. We're looking into it!",
  },
});

const UNEXPECTED_ERROR_MESSAGE = messages.unexpectedError;

const InnerPaymentElementDemo = ({state}: {|state: State|}) => {
  const options = {
    paymentMethodOrder: LOCATIONS[state.location].paymentMethodOrder,
    business: {
      name: 'Stripe Payment Element Demo',
    },
  };

  return (
    <Box
      style={{maxWidth: '490px'}}
      flexBasis="100%"
      padding={12}
      margin={{top: 64}}
    >
      <PaymentElement options={options} />
    </Box>
  );
};

// Since Payment Element is still in beta, will use a hardcoded key for now.
// This key need to match the key used for API call: payments-web-bots+payment-intents@stripe.com test account
// Can probably update to useAccount()'s publishbale key later.
const stripe = loadStripe('pk_test_Sds05L2renhqjHCunKB9i5i2');

export const PaymentElementAppearanceDemo = () => {
  const {api} = useApi();
  const [state, dispatch] = useReducer(reducer, getInitialState());
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  const intl = useIntl();
  const localizedErrorMessage = intl.formatMessage(UNEXPECTED_ERROR_MESSAGE);

  useEffect(() => {
    if (state.clientSecret) {
      return;
    }

    api
      .createPaymentIntent({
        payment_method_types: LOCATIONS[state.location].paymentMethodTypes,
        is_payment_element: true,
        payment_element_currency: LOCATIONS[state.location].currency,
      })
      .then((response) => {
        if (!response.clientSecret) {
          const domain = window.location.hostname || 'stripe.com';
          const errorMessage = domain.match('dev.stripe.me')
            ? 'This demo does not work locally.'
            : localizedErrorMessage;

          setError(errorMessage);
          setLoading(false);
          return;
        }

        dispatch({kind: 'SECRET_CHANGED', clientSecret: response.clientSecret});
        setLoading(false);
      });
  }, [api, state.location, state.clientSecret, localizedErrorMessage]);

  const options = {
    locale: LOCATIONS[state.location].locale,
    clientSecret: state.clientSecret,
    appearance: THEMES[state.theme].appearance,
  };

  return (
    <Box padding={{top: 20}}>
      <Card shadow="keyline">
        <Group direction="vertical" background={THEMES[state.theme].background}>
          <Box flex={{justifyContent: 'center'}} style={{minHeight: '450px'}}>
            {loading && (
              <Box style={{position: 'absolute'}}>
                <Spinner size="large" style={{padding: '200px'}} />
              </Box>
            )}

            {state.clientSecret && (
              <Elements key={state.location} stripe={stripe} options={options}>
                <InnerPaymentElementDemo state={state} />
              </Elements>
            )}
            {error && (
              <Box
                flex={{justifyContent: 'center'}}
                style={{paddingTop: '150px'}}
                margin={{top: 80}}
              >
                <Group direction="horizontal" spacing={8}>
                  <Icon icon="warning" color="gray" size={14} />
                  <Text size={15} color="gray600">
                    {error}
                  </Text>
                </Group>
              </Box>
            )}
          </Box>
          <Box flex={{justifyContent: 'flex-end'}}>
            <LocationDropdown state={state} dispatch={dispatch} />
          </Box>
        </Group>
        <SourceCodeBlock state={state} dispatch={dispatch} />
      </Card>
    </Box>
  );
};
